import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import { FdrThemeRecommender } from 'components/domain/fdr-page-components/recommenders/theme/fdr-theme-recommender';
import useFdrPageComponentsFetch from 'components/domain/fdr-page-components/useFdrPageComponentsFetch';
import { mapThemeGeoRecommenderResponseToThemeGeo } from 'components/domain/fdr-theme-geo/fdr-theme-geo.utils';
import { FdrThemeGeoPaginatedProductsDocument } from 'schemas/query/theme-geo/fdr-theme-geo-paginated-products.generated';
import { GEO_THEME_BG_COLOR, GEO_THEMES_FONT_COLOR } from './fdr-geo.constants';
import { TFdrThGeoModuleObject } from './fdr-geo.types';
import { NavigationIds } from './navigation/fdr-geo-navigation.types';

interface IFdrThemeGeoRecommender {
  geoId: string;
  themeId: string;
  themeGeoPages: Omit<TFdrThGeoModuleObject['themeGeoPages'], '__typename'>;
  geoObject?: string;
}

const FdrThemeGeoRecommender: React.FC<IFdrThemeGeoRecommender> = ({
  geoId,
  themeId,
  themeGeoPages,
  geoObject,
}) => {
  const [t] = useTranslation('geo');

  const mapVariables = useCallback(
    (page, perPage) => {
      return {
        input: { geoId, themeId },
        pagination: {
          page,
          perPage,
        },
      };
    },
    [geoId, themeId]
  );

  const { fetchItems } = useFdrPageComponentsFetch(
    FdrThemeGeoPaginatedProductsDocument,
    mapVariables,
    mapThemeGeoRecommenderResponseToThemeGeo
  );

  if (!themeGeoPages.themeGeoPages?.length) {
    return null;
  }

  return (
    <FdrThemeRecommender
      total={themeGeoPages.pagination.total}
      title={t('geo:themes.title', { geoObject })}
      fontColor={GEO_THEMES_FONT_COLOR}
      bgColor={GEO_THEME_BG_COLOR}
      items={
        themeGeoPages.themeGeoPages.map(themeGeo => ({
          ...themeGeo,
          id: [themeGeo.geo.id, themeGeo.theme.id].join('-'),
          link: themeGeo.webMeta.link,
        })) || []
      }
      id={NavigationIds.THEMES}
      fetchItems={fetchItems}
    />
  );
};

export default FdrThemeGeoRecommender;
